import {
  Button,
  Checkbox,
  Divider,
  Form,
  Input,
  InputNumber,
  Modal,
  notification,
  Select,
  Space,
  Tabs,
  Typography,
} from 'antd'
import { useMemo, useState } from 'react'
import { useGoldenPromptsAdminListQuery } from '../GoldenPrompts/goldenPromptsAdminQueries'
import { useMcpMocksListQuery } from '../McpMocks/mcpMocksAdminQueries'
import { useMcpToolsListQuery } from '../McpTools/mcpToolsAdminQueries'
import { useRubricsListQuery } from '../Rubrics/rubricsAdminQueries'
import { useTagsListQuery } from '../Tags/tagsAdminQueries'
import McpToolMultiSelect, {
  SELECTED_LABEL_STYLE,
} from './McpToolMultiSelect'
import {
  applyGoldenPromptToContext,
  buildCaseCreateInput,
  buildCaseTagsUpdateInput,
  buildCaseUpdateInput,
  caseBodyEqual,
  caseFormFromBody,
  CaseFormState,
  EvalCase,
  emptyCaseForm,
  finalPromptText,
  prepareCaseBody,
  tagIdsEqual,
} from './casesAdminModel'
import {
  useCreateCaseMutation,
  useSetCaseTagsMutation,
  useUpdateCaseMutation,
} from './casesAdminQueries'

type Props = {
  open: boolean
  onClose: () => void
  /** When provided the modal edits this case; otherwise it creates a new one. */
  evalCase?: EvalCase
}

// The picker lists are admin-scale; load the first page at the backend's cap.
const PICKER_FILTERS = { page: 1, pageSize: 50 }
// Seed cases from original golden prompts only — mutants (mutation variants
// with an ancestor) are excluded via parents_only.
const GOLDEN_PROMPT_FILTERS = {
  promptType: 'any' as const,
  parentsOnly: true,
  page: 1,
  pageSize: 50,
}
const MONOSPACE: React.CSSProperties = { fontFamily: 'monospace' }

/** Keep golden-prompt option labels to a single readable line in the dropdown. */
const truncate = (text: string, max = 90): string =>
  text.length > max ? `${text.slice(0, max).trimEnd()}…` : text

const CaseEditorModal = ({ open, onClose, evalCase }: Props) => {
  const isEdit = evalCase != null
  const [form, setForm] = useState<CaseFormState>(() =>
    evalCase
      ? caseFormFromBody(evalCase.name, evalCase.body, evalCase.tags)
      : emptyCaseForm(),
  )
  const set = <K extends keyof CaseFormState>(key: K, value: CaseFormState[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }))

  // Most cases just pick a golden prompt, so the raw context JSON is hidden by
  // default when creating. Editing an existing case shows it — the context may
  // have been hand-built with history or seeded tool turns worth seeing.
  const [showRawContext, setShowRawContext] = useState(isEdit)

  const createMutation = useCreateCaseMutation()
  const updateMutation = useUpdateCaseMutation()
  const setTagsMutation = useSetCaseTagsMutation()
  const saving =
    createMutation.isLoading ||
    updateMutation.isLoading ||
    setTagsMutation.isLoading

  const { data: scenarioData } = useMcpMocksListQuery(PICKER_FILTERS)
  const { data: rubricData } = useRubricsListQuery(PICKER_FILTERS)
  const { data: mcpToolData } = useMcpToolsListQuery()
  const { data: tagData } = useTagsListQuery(PICKER_FILTERS)
  const { data: goldenPromptData } =
    useGoldenPromptsAdminListQuery(GOLDEN_PROMPT_FILTERS)
  const mcpTools = mcpToolData?.tools ?? []
  const tagOptions =
    tagData?.tags.map((t) => ({ label: t.name, value: t.tag_id })) ?? []

  // Baseline body for change detection: the original case body run through the
  // same pipeline the editor uses, so an untouched form compares equal. Lets us
  // skip a body-versioning PUT when only the (version-free) tags changed (#92).
  const baselineBody = useMemo(
    () =>
      evalCase
        ? prepareCaseBody(
            caseFormFromBody(evalCase.name, evalCase.body, evalCase.tags),
          ).body
        : undefined,
    [evalCase],
  )
  const scenarioOptions =
    scenarioData?.scenarios.map((s) => ({ label: s.name, value: s.name })) ?? []
  const rubricOptions =
    rubricData?.rubrics.map((r) => ({ label: r.name, value: r.name })) ?? []
  const goldenPromptOptions =
    goldenPromptData?.items.map((p) => ({
      label: truncate(p.prompt_text),
      value: p.prompt_id,
    })) ?? []

  // Preview of the current prompt (final user message) for the folded view.
  const promptPreview = finalPromptText(form.contextJson) ?? ''

  // Drive the Save button off the same validation the save handler uses, so the
  // button stays inactive until every save criterion is met. `prepareCaseBody`
  // returns a single actionable reason; the name is checked separately because
  // it lives outside the case body (and is immutable when editing).
  const nameError = !isEdit && !form.name.trim() ? 'Name is required.' : undefined
  const { error: bodyError } = prepareCaseBody(form)
  const saveBlockedReason = nameError ?? bodyError

  // Drop the chosen golden prompt's text into the final (user) context message
  // and record the lineage (#79).
  const insertGoldenPrompt = (promptId: string) => {
    const prompt = goldenPromptData?.items.find((p) => p.prompt_id === promptId)
    if (prompt) {
      setForm((prev) => ({
        ...prev,
        contextJson: applyGoldenPromptToContext(prev.contextJson, prompt.prompt_text),
        goldenPromptId: promptId,
      }))
    }
  }

  // A hand-edit to the raw context invalidates the lineage claim, so clear it.
  const editRawContext = (contextJson: string) =>
    setForm((prev) => ({ ...prev, contextJson, goldenPromptId: null }))

  const handleClose = () => {
    setForm(
      evalCase
        ? caseFormFromBody(evalCase.name, evalCase.body, evalCase.tags)
        : emptyCaseForm(),
    )
    onClose()
  }

  const handleOk = async () => {
    if (!isEdit && !form.name.trim()) {
      notification.error({ message: 'Invalid case', description: 'Name is required.' })
      return
    }
    const { body, error } = prepareCaseBody(form)
    if (error || !body) {
      notification.error({ message: 'Invalid case', description: error })
      return
    }
    if (isEdit && evalCase) {
      // Tags live on the case entity and save version-free; the body only PUTs
      // when it actually changed, so a tag-only edit doesn't version the case.
      const bodyChanged = !baselineBody || !caseBodyEqual(body, baselineBody)
      const tagsChanged = !tagIdsEqual(
        evalCase.tags.map((t) => t.tag_id),
        form.tagIds,
      )
      try {
        if (bodyChanged) {
          await updateMutation.mutateAsync(buildCaseUpdateInput(evalCase, body))
        }
        if (tagsChanged) {
          await setTagsMutation.mutateAsync(
            buildCaseTagsUpdateInput(evalCase, form.tagIds),
          )
        }
        handleClose()
      } catch {
        // The mutation hooks already surfaced the error via notification.
      }
      return
    }
    createMutation.mutate(buildCaseCreateInput(form, body), {
      onSuccess: handleClose,
    })
  }

  return (
    <Modal
      open={open}
      onCancel={handleClose}
      onOk={handleOk}
      okText="Save"
      okButtonProps={{ disabled: saveBlockedReason != null }}
      title={isEdit ? 'Edit Case' : 'New Case'}
      width={860}
      confirmLoading={saving}
      destroyOnClose
    >
      <Form layout="vertical">
        <Form.Item
          label="Name"
          required
          // The name is the key runs pin a case by; the backend cannot rename on
          // update, so it is read-only when editing. Kept above the tabs so it is
          // always visible.
          extra={
            isEdit
              ? 'The name is immutable; saving appends a new version.'
              : undefined
          }
          style={{ marginBottom: 12 }}
        >
          <Input
            value={form.name}
            onChange={(e) => set('name', e.target.value)}
            placeholder="e.g. schedule-mow-happy-path"
            disabled={isEdit}
          />
        </Form.Item>
        <Form.Item
          label="Tags"
          // Curated picker from the central tag list; tags are case-entity
          // metadata, saved separately from (and without versioning) the body.
          extra={
            isEdit
              ? 'Tag changes save immediately and do not version the case.'
              : undefined
          }
          style={{ marginBottom: 12 }}
        >
          <Select
            mode="multiple"
            allowClear
            value={form.tagIds}
            onChange={(value) => set('tagIds', value)}
            options={tagOptions}
            placeholder="Tag this case (manage tags under Tags)"
            notFoundContent="No tags — create some under Tags first."
            optionFilterProp="label"
          />
        </Form.Item>
        <Tabs
          defaultActiveKey="inputs"
          items={[
            {
              key: 'inputs',
              label: 'Inputs',
              children: (
                <>
                  <Form.Item label="Scenario" required style={{ marginBottom: 12 }}>
                    <Select
                      showSearch
                      value={form.scenario || undefined}
                      onChange={(value) => set('scenario', value)}
                      options={scenarioOptions}
                      placeholder="Pick an MCP mock scenario"
                      notFoundContent="No scenarios — create one under MCP Mocks first."
                    />
                  </Form.Item>

                  <Form.Item
                    label="Context"
                    required
                    extra="Pick a golden prompt to use as the prompt, or edit the raw conversation context. The final message must be a user turn."
                    style={{ marginBottom: 12 }}
                  >
                    <Space direction="vertical" size={8} style={{ width: '100%' }}>
                      <Select
                        showSearch
                        allowClear
                        value={null}
                        onChange={(value) => value && insertGoldenPrompt(value)}
                        options={goldenPromptOptions}
                        placeholder="Set the prompt from a golden prompt…"
                        notFoundContent="No golden prompts yet."
                        style={{ width: '100%' }}
                      />
                      {/* Show the current prompt as a label so picking one gives
                          visible feedback; redundant once the raw editor is open. */}
                      {!showRawContext && promptPreview.trim() !== '' && (
                        <span
                          style={{
                            ...SELECTED_LABEL_STYLE,
                            maxWidth: '100%',
                            whiteSpace: 'normal',
                            wordBreak: 'break-word',
                          }}
                        >
                          {promptPreview}
                        </span>
                      )}
                      {form.goldenPromptId && (
                        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                          Linked to a golden prompt — editing raw context clears
                          this link.
                        </Typography.Text>
                      )}
                      <Button
                        type="link"
                        size="small"
                        style={{ padding: 0 }}
                        onClick={() => setShowRawContext((prev) => !prev)}
                      >
                        {showRawContext
                          ? 'Hide raw context'
                          : 'Edit raw context (JSON)'}
                      </Button>
                      {showRawContext && (
                        <Input.TextArea
                          value={form.contextJson}
                          onChange={(e) => editRawContext(e.target.value)}
                          autoSize={{ minRows: 6, maxRows: 16 }}
                          style={MONOSPACE}
                        />
                      )}
                    </Space>
                  </Form.Item>
                </>
              ),
            },
            {
              key: 'outcome',
              label: 'Outcome',
              children: (
                <>
                  <Typography.Text type="secondary">
                    Define at least one outcome check, or enable a judge below to
                    score the case by rubric.
                  </Typography.Text>

                  <Form.Item
                    label="Answer must contain"
                    style={{ marginBottom: 12, marginTop: 8 }}
                  >
                    <Select
                      mode="tags"
                      value={form.answerMustContain}
                      onChange={(value) => set('answerMustContain', value)}
                      placeholder="Phrases the final answer must include"
                      open={false}
                    />
                  </Form.Item>

                  <Form.Item label="Forbidden writes" style={{ marginBottom: 12 }}>
                    <McpToolMultiSelect
                      tools={mcpTools}
                      value={form.forbiddenWrites}
                      onChange={(value) => set('forbiddenWrites', value)}
                      placeholder="Search MCP tools that must NOT be written"
                    />
                  </Form.Item>

                  <Form.Item
                    label="Writes"
                    extra='JSON array of expected writes, e.g. [{"tool": "create_job", "args_match": {"day": "Tuesday"}, "count": 1}]'
                    style={{ marginBottom: 12 }}
                  >
                    <Input.TextArea
                      value={form.writesJson}
                      onChange={(e) => set('writesJson', e.target.value)}
                      autoSize={{ minRows: 3, maxRows: 12 }}
                      style={MONOSPACE}
                    />
                  </Form.Item>

                  <Divider orientation="left" style={{ margin: '4px 0' }}>
                    <Checkbox
                      checked={form.trajectoryEnabled}
                      onChange={(e) => set('trajectoryEnabled', e.target.checked)}
                    >
                      Trajectory (optional)
                    </Checkbox>
                  </Divider>
                  {form.trajectoryEnabled && (
                    <>
                      <Form.Item label="Must call" style={{ marginBottom: 12 }}>
                        <McpToolMultiSelect
                          tools={mcpTools}
                          value={form.mustCall}
                          onChange={(value) => set('mustCall', value)}
                          placeholder="Search MCP tools that must be called"
                        />
                      </Form.Item>
                      <Form.Item label="Must not call" style={{ marginBottom: 12 }}>
                        <McpToolMultiSelect
                          tools={mcpTools}
                          value={form.mustNotCall}
                          onChange={(value) => set('mustNotCall', value)}
                          placeholder="Search MCP tools that must not be called"
                        />
                      </Form.Item>
                      <Form.Item label="Max tool calls" style={{ marginBottom: 12 }}>
                        <InputNumber
                          min={0}
                          value={form.maxToolCalls ?? undefined}
                          onChange={(value) => set('maxToolCalls', value ?? null)}
                          placeholder="No limit"
                        />
                      </Form.Item>
                    </>
                  )}

                  <Divider orientation="left" style={{ margin: '4px 0' }}>
                    <Checkbox
                      checked={form.judgeEnabled}
                      onChange={(e) => set('judgeEnabled', e.target.checked)}
                    >
                      Judge (optional)
                    </Checkbox>
                  </Divider>
                  {form.judgeEnabled && (
                    <Space size="large" align="start">
                      <Form.Item label="Rubric" required style={{ marginBottom: 0 }}>
                        <Select
                          showSearch
                          style={{ width: 320 }}
                          value={form.rubric || undefined}
                          onChange={(value) => set('rubric', value)}
                          options={rubricOptions}
                          placeholder="Pick a rubric"
                          notFoundContent="No rubrics — create one under Rubrics first."
                        />
                      </Form.Item>
                      <Form.Item label="Threshold" required style={{ marginBottom: 0 }}>
                        <InputNumber
                          min={0}
                          max={1}
                          step={0.05}
                          value={form.threshold}
                          onChange={(value) => set('threshold', value ?? 0)}
                        />
                      </Form.Item>
                    </Space>
                  )}
                </>
              ),
            },
          ]}
        />
        {/* Surface why Save is disabled so the inactive button isn't a dead end. */}
        {saveBlockedReason && (
          <Typography.Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
            {saveBlockedReason}
          </Typography.Text>
        )}
      </Form>
    </Modal>
  )
}

export default CaseEditorModal
